import type { Identifier } from "ra-core";

import type { Actor } from "./canAccess";

/**
 * Almikia Epic EC2b (TASK-124): the `contact_expediente` right. It MIRRORS
 * `private.can_access_contact_expediente()` (TASK-123) and never replaces it: the expediente
 * (sensitive PLD / contract data) is readable and writable by exactly five audiences, the
 * contact's responsable, her lider, direccion, administracion and compliance. lead_manager (unless
 * she is the responsable), contabilidad and auditoria never get it.
 */
export const CONTACT_EXPEDIENTE_RESOURCE = "contact_expediente";

/** Roles that can be the `responsable` of a contact (`private.is_commercial()`). */
const COMMERCIAL_ROLES = ["direccion", "lead_manager", "lider", "asesora"];

/** Roles that reach every contact's expediente. */
const EXPEDIENTE_STAFF_ROLES = ["direccion", "administracion", "compliance"];

/**
 * The record-level gate, shared with the FakeRest provider: `contactSalesId` is the contact's
 * `sales_id` (the responsable) and `isInLiderTeam` says whether that responsable belongs to the
 * acting lider's team (resolved by the caller, the way `private.in_lider_team` is in SQL).
 */
export const canActorAccessExpedienteOf = (
  actor: Pick<Actor, "role" | "id"> | null | undefined,
  contactSalesId: Identifier | null | undefined,
  isInLiderTeam: boolean,
): boolean => {
  const role = actor?.role;
  if (!role) return false;
  if (EXPEDIENTE_STAFF_ROLES.includes(role)) return true;
  if (contactSalesId == null) return false;
  if (
    actor?.id != null &&
    contactSalesId === actor.id &&
    COMMERCIAL_ROLES.includes(role)
  ) {
    return true;
  }
  return role === "lider" && isInLiderTeam;
};

/**
 * `canAccess` branch for the `contact_expediente` right. Direccion, administracion and compliance:
 * yes. asesora: yes (section-level) or on a record she owns. lider: yes (her team is resolved by
 * the provider gate, which is the authority). lead_manager: only on a record she owns (she is then
 * the responsable). contabilidad, auditoria and an identity without a role: no. Nothing is ever
 * deleted or exported.
 */
export const canAccessContactExpediente = (
  actor: Actor | null | undefined,
  params: { action: string; record?: Record<string, any> },
): boolean => {
  const role = actor?.role;
  if (!role || params.action === "delete" || params.action === "export") {
    return false;
  }
  if (EXPEDIENTE_STAFF_ROLES.includes(role)) return true;
  if (role === "lider") return true;
  const ownsRecord =
    params.record != null &&
    actor?.id != null &&
    params.record.sales_id === actor.id;
  if (role === "asesora") return params.record == null || ownsRecord;
  if (role === "lead_manager") return ownsRecord;
  return false;
};
